<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="api-mesh-container">
  <!-- Left Sidebar Menu -->
  <aside class="sidebar">
    <div class="sidebar-header">
      <h2>API Mesh</h2>
    </div>
    <nav class="sidebar-nav">
      <ul class="nav-list">

        <li class="nav-item"
            [class.active]="activeSection === 'overview'"
            (click)="setActiveSection('overview')">
          <!-- <span class="nav-icon">📊</span> -->
          <span class="nav-text">Overview</span>
        </li>

        <li class="nav-item"
            [class.active]="activeSection === 'documents'"
            (click)="toggleDocumentsExpanded()">
          <span class="nav-text">Documents</span>
          <span class="expand-icon" [class.expanded]="isDocumentsExpanded">▶</span>
        </li>

        <!-- Dynamic Documentation Menu -->
        <div class="sub-menu" [class.visible]="isDocumentsExpanded && documentationConfig">
          <ng-container *ngFor="let section of documentationConfig?.sections">
            <li class="nav-item level-1"
                [class.active]="activeSection === section.route || activeSection === section.id"
                (click)="handleDocumentationSectionClick(section)">
              <span class="nav-text">{{ section.title }}</span>
              <span *ngIf="section.items"
                    class="expand-icon"
                    [class.expanded]="isDocumentationSectionExpanded(section.id)">▶</span>
            </li>

            <!-- Nested Documentation Items -->
            <div *ngIf="section.items" class="sub-menu" [class.visible]="isDocumentationSectionExpanded(section.id)">
              <ng-container *ngFor="let item of section.items">
                <li class="nav-item level-2"
                    [class.active]="activeSection === item.route"
                    (click)="handleDocumentationItemClick(item)">
                  <span class="nav-text">{{ item.title }}</span>
                  <span *ngIf="item.items"
                        class="expand-icon"
                        [class.expanded]="isDocumentationSectionExpanded(item.id)">▶</span>
                </li>

                <!-- Third Level Items -->
                <div *ngIf="item.items" class="sub-menu" [class.visible]="isDocumentationSectionExpanded(item.id)">
                  <ng-container *ngFor="let subItem of item.items">
                    <li class="nav-item level-3"
                        [class.active]="activeSection === subItem.route"
                        (click)="handleDocumentationItemClick(subItem)">
                      <span class="nav-text">{{ subItem.title }}</span>
                      <span *ngIf="subItem.items"
                            class="expand-icon"
                            [class.expanded]="isDocumentationSectionExpanded(subItem.id)">▶</span>
                    </li>

                    <!-- Fourth Level Items -->
                    <div *ngIf="subItem.items" class="sub-menu" [class.visible]="isDocumentationSectionExpanded(subItem.id)">
                      <li *ngFor="let subSubItem of subItem.items"
                          class="nav-item level-4"
                          [class.active]="activeSection === subSubItem.route"
                          (click)="handleDocumentationItemClick(subSubItem)">
                        <span class="nav-text">{{ subSubItem.title }}</span>
                      </li>
                    </div>
                  </ng-container>
                </div>
              </ng-container>
            </div>
          </ng-container>
        </div>
        <li class="nav-item"
            [class.active]="activeSection === 'api-explorer'"
            (click)="setActiveSection('api-explorer')">
          <!-- <span class="nav-icon">🔍</span> -->
          <span class="nav-text">API Explorer</span>
        </li>
        <li class="nav-item"
            [class.active]="activeSection === 'samples'"
            (click)="handleSamplesMenuClick()">
          <span class="nav-text">Samples</span>
          <span class="expand-icon" [class.expanded]="isSamplesExpanded">▶</span>
        </li>
        <div class="sub-menu" [class.visible]="isSamplesExpanded">
          <li class="nav-item level-1"
              [class.active]="activeSection === 'sample-backend-nodejs'"
              (click)="setActiveSection('sample-backend-nodejs')">
            <span class="nav-text">API Mesh Integration<br/> <b>Node.js + TypeScript</b> Backend </span>
          </li>
          <li class="nav-item level-1"
              [class.active]="activeSection === 'sample-typescript'"
              (click)="setActiveSection('sample-typescript')">
            <span class="nav-text">API Mesh Integration<br/> <b>TypeScript</b> Frontend</span>
          </li>
          <li class="nav-item level-1"
              [class.active]="activeSection === 'sample-javascript'"
              (click)="setActiveSection('sample-javascript')">
            <span class="nav-text">API Mesh Integration<br/> <b>JavaScript</b> Frontend</span>
          </li>
          <li class="nav-item level-1"
              [class.active]="activeSection === 'sample-angular'"
              (click)="setActiveSection('sample-angular')">
            <span class="nav-text">API Mesh Integration<br/> <b>Angular</b> Frontend</span>
          </li>
        </div>
      </ul>
    </nav>
  </aside>

  <!-- Main Content Area -->
  <article class="article-content" [class.split-layout]="isDocumentationSection(activeSection)">
    <!-- Left Content Panel (for non-documentation sections or when no document is selected) -->
    <div class="left-content-panel" [class.hidden]="isDocumentationSection(activeSection)">
      <div class="content-header">
        <h1 [innerHTML]="getSectionTitle()"></h1>
        <p class="subtitle">{{ getSectionSubtitle() }}</p>
      </div>

      <!-- Overview Section -->
      <div class="section-content" *ngIf="activeSection === 'overview'">
        <app-api-mesh-overview></app-api-mesh-overview>
      </div>

      <!-- API Explorer Section -->
      <div class="section-content" *ngIf="activeSection === 'api-explorer'">
        <!-- API Explorer List View -->
        <div *ngIf="!showDynamicViewer">
          <app-api-mesh-explorer></app-api-mesh-explorer>
        </div>

        <!-- Dynamic API Viewer -->
        <div *ngIf="showDynamicViewer && selectedOperation" class="dynamic-viewer-container">
          <app-dynamic-api-viewer
            [operation]="selectedOperation"
            [isEmbedded]="true"
            (backToExplorer)="goBackToExplorer()">
          </app-dynamic-api-viewer>
        </div>
      </div>

      <!-- Samples Section -->
      <div class="section-content" *ngIf="activeSection === 'samples' || activeSection === 'sample-typescript' || activeSection === 'sample-javascript' || activeSection === 'sample-angular' || activeSection === 'sample-backend-nodejs'">
        <app-api-mesh-samples [currentSection]="activeSection"></app-api-mesh-samples>
      </div>
    </div>

    <!-- Right Content Panel (for documentation) -->
    <div class="right-content-panel" *ngIf="isDocumentationSection(activeSection) && documentationConfig">
      <div class="document-panel-header">
        <h2>{{ getDocumentTitle() }}</h2>
      </div>

      <div class="document-panel-content">
        <app-documentation-viewer
          class="split-panel-mode"
          [documentationRoute]="activeSection">
        </app-documentation-viewer>
      </div>
    </div>
  </article>
</div>
